// contexts/UnitContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const UnitContext = createContext();

export function UnitProvider({ children }) {
  const [unitSystem, setUnitSystem] = useState('english');
  const [timeFormat, setTimeFormat] = useState('12h'); // Add time format preference
  const [loading, setLoading] = useState(true);
  const { currentUser, userObjectId } = useAuth();

  // Fetch user's unit preference when they log in
  useEffect(() => {
    const fetchUnitPreference = async () => {
      if (currentUser && userObjectId) {
        try {
          setLoading(true);
          const token = await currentUser.getIdToken();
          const response = await axios.get(`/api/diver/${userObjectId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          if (response.data && response.data.unitPreference) {
            setUnitSystem(response.data.unitPreference);
          }
          
          // Also fetch time format preference if available
          if (response.data && response.data.timeFormat) {
            setTimeFormat(response.data.timeFormat);
          }
        } catch (error) {
          console.error('Error fetching user preferences:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchUnitPreference();
  }, [currentUser, userObjectId]);

  // Update user's unit preference in the database
  const updateUnitPreference = async (newUnitSystem) => {
    if (!currentUser || !userObjectId) return false;

    try {
      const token = await currentUser.getIdToken();
      await axios.patch(
        `/api/diver/${userObjectId}`,
        { unitPreference: newUnitSystem },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setUnitSystem(newUnitSystem);
      return true;
    } catch (error) {
      console.error('Error updating unit preference:', error);
      return false;
    }
  };
  
  // Update user's time format preference in the database
  const updateTimeFormat = async (newTimeFormat) => {
    if (!currentUser || !userObjectId) return false;

    try {
      const token = await currentUser.getIdToken();
      await axios.patch(
        `/api/diver/${userObjectId}`,
        { timeFormat: newTimeFormat },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setTimeFormat(newTimeFormat);
      return true;
    } catch (error) {
      console.error('Error updating time format preference:', error);
      return false;
    }
  };

  // Conversion utilities
  const convert = {
    // Length conversions
    length: {
      toMetric: (feet) => feet * 0.3048,
      toEnglish: (meters) => meters / 0.3048,
      format: (value) => unitSystem === 'metric' ? 
        `${value.toFixed(1)} m` : 
        `${value.toFixed(1)} ft`
    },
    
    // Temperature conversions
    temperature: {
      toMetric: (fahrenheit) => (fahrenheit - 32) * 5/9,
      toEnglish: (celsius) => (celsius * 9/5) + 32,
      format: (value) => unitSystem === 'metric' ? 
        `${value.toFixed(1)}°C` : 
        `${value.toFixed(1)}°F`
    },
    
    // Pressure conversions (psi to bar)
    pressure: {
      toMetric: (psi) => psi * 0.0689476,
      toEnglish: (bar) => bar / 0.0689476,
      format: (value) => unitSystem === 'metric' ? 
        `${value.toFixed(1)} bar` : 
        `${value.toFixed(0)} psi`
    },
    
    // Weight conversions
    weight: {
      toMetric: (pounds) => pounds * 0.453592,
      toEnglish: (kilograms) => kilograms / 0.453592,
      format: (value) => unitSystem === 'metric' ? 
        `${value.toFixed(1)} kg` : 
        `${value.toFixed(1)} lbs`
    },
    
    // Volume conversions
    volume: {
      toMetric: (cuft) => cuft * 28.3168,
      toEnglish: (liters) => liters / 28.3168,
      format: (value) => unitSystem === 'metric' ? 
        `${value.toFixed(0)} L` : 
        `${value.toFixed(1)} cu ft`
    },
    
    // Time format conversions
    time: {
      format: (date, options = {}) => {
        if (!date) return '';
        
        const dateObj = date instanceof Date ? date : new Date(date);
        
        if (isNaN(dateObj.getTime())) {
          console.error('Invalid date provided to time formatter:', date);
          return 'Invalid Date';
        }
        
        const defaultOptions = {
          hour: 'numeric',
          minute: 'numeric',
          hour12: timeFormat === '12h',
          ...options
        };
        
        return dateObj.toLocaleTimeString(undefined, defaultOptions);
      },
      
      formatWithSeconds: (date) => {
        return convert.time.format(date, {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
      },
      
      formatDate: (date, includeTime = false) => {
        if (!date) return '';
        
        const dateObj = date instanceof Date ? date : new Date(date);
        
        if (isNaN(dateObj.getTime())) {
          console.error('Invalid date provided to date formatter:', date);
          return 'Invalid Date';
        }
        
        const dateOptions = { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        };
        
        if (includeTime) {
          return `${dateObj.toLocaleDateString(undefined, dateOptions)} ${convert.time.format(dateObj)}`;
        }
        
        return dateObj.toLocaleDateString(undefined, dateOptions);
      },
      
      // For displaying time ranges
      formatTimeRange: (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        
        const start = convert.time.format(startDate);
        const end = convert.time.format(endDate);
        
        return `${start} - ${end}`;
      }
    }
  };

  const value = {
    unitSystem,
    timeFormat,
    loading,
    updateUnitPreference,
    updateTimeFormat,
    convert,
    isMetric: unitSystem === 'metric',
    is24Hour: timeFormat === '24h'
  };

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
}

export const useUnits = () => useContext(UnitContext);
